import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { useTenant } from '../context/TenantContext';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import './MainSection.css';
import { isToday, format } from 'date-fns';

function MainSection() {
  const [patientsCount, setPatientsCount] = useState(0);
  const [totalAppointments, setTotalAppointments] = useState(0);
  const [todaysAppointments, setTodaysAppointments] = useState(0);
  const [chartData, setChartData] = useState([]);
  const { tenantId, loading } = useTenant();

  useEffect(() => {
    if (loading || !tenantId) return;

    // Fetch Patients
    const patientsQuery = query(collection(db, 'patients'), where("tenantId", "==", tenantId));
    const unsubscribePatients = onSnapshot(patientsQuery, (snapshot) => {
      setPatientsCount(snapshot.size);
    });

    // Fetch Appointments
    const appointmentsQuery = query(collection(db, 'appointments'), where("tenantId", "==", tenantId));
    const unsubscribeAppointments = onSnapshot(appointmentsQuery, (snapshot) => {
      setTotalAppointments(snapshot.size);
      
      let todaysCount = 0;
      const appointmentsByDate = {};

      snapshot.forEach(doc => {
        const data = doc.data();
        if (data.datetime) {
          const date = data.datetime.toDate();
          if (isToday(date)) {
            todaysCount++;
          }
          
          const dateStr = format(date, 'MMM dd');
          appointmentsByDate[dateStr] = (appointmentsByDate[dateStr] || 0) + 1;
        }
      });

      setTodaysAppointments(todaysCount);

      // Create last 7 days array and fill it
      const last7Days = Array.from({length: 7}, (_, i) => {
        const d = new Date();
        d.setDate(d.getDate() - (6 - i));
        return format(d, 'MMM dd');
      });

      const formattedData = last7Days.map(dateStr => ({
        name: dateStr,
        Appointments: appointmentsByDate[dateStr] || 0
      }));

      setChartData(formattedData);
    });

    return () => {
      unsubscribePatients();
      unsubscribeAppointments();
    };
  }, [tenantId, loading]);

  return (
    <section className="analytics-main-section">
      <div className="analytics-summary-cards">
        <div className="analytics-card">
          <h3 className="analytics-card-title">Total Patients</h3>
          <p className="analytics-card-value">{patientsCount}</p>
          <span className="analytics-card-subtext">+ Active records</span>
        </div>
        <div className="analytics-card">
          <h3 className="analytics-card-title">Total Appointments</h3>
          <p className="analytics-card-value">{totalAppointments}</p>
          <span className="analytics-card-subtext">All time scheduled</span>
        </div>
        <div className="analytics-card">
          <h3 className="analytics-card-title">Today's Appointments</h3>
          <p className="analytics-card-value">{todaysAppointments}</p>
          <span className="analytics-card-subtext">Pending for today</span>
        </div>
      </div>

      <div className="analytics-charts-container">
        <div className="analytics-chart-card main-chart">
          <h3 className="analytics-card-title">Appointments Over Time</h3>
          <p className="analytics-card-subtext">Real time updates for the last 7 days</p>
          <div style={{ width: '100%', height: 320, marginTop: '20px' }}>
            <ResponsiveContainer>
              <AreaChart
                data={chartData}
                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#078158" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#078158" stopOpacity={0.0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#999', fontSize: 12}} dy={15} />
                <YAxis axisLine={false} tickLine={false} tick={{fill: '#999', fontSize: 12}} dx={-10} allowDecimals={false} />
                <Tooltip 
                   contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,0.1)' }}
                   cursor={{ stroke: '#078158', strokeWidth: 1, strokeDasharray: '3 3' }}
                />
                <Area type="monotone" dataKey="Appointments" stroke="#078158" strokeWidth={3} fillOpacity={1} fill="url(#colorUv)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="analytics-secondary-cards">
          <div className="analytics-chart-card">
            <h3 className="analytics-card-title">Patient Demographics</h3>
            <p className="analytics-card-subtext">Age Distribution</p>
            <div className="dummy-chart-placeholder">
              <div className="dummy-bar" style={{height: '60%'}}></div>
              <div className="dummy-bar" style={{height: '80%'}}></div>
              <div className="dummy-bar" style={{height: '100%'}}></div>
              <div className="dummy-bar" style={{height: '90%'}}></div>
              <div className="dummy-bar" style={{height: '50%'}}></div>
              <div className="dummy-bar" style={{height: '30%'}}></div>
            </div>
            <p className="analytics-card-subtext text-center mt-3">Monthly overview</p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default MainSection;